import React, { useState, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FaSearch, FaTimes, FaChevronDown, FaCheck } from 'react-icons/fa';

/**
 * GroupedCheckboxDropdown — reusable multi-select dropdown with group headers.
 *
 * Props:
 *   groups      — [{ key, label, color?, items: [{ value, label, color?, meta? }] }]
 *   selectedIds — array of currently selected item values
 *   onChange    — (nextIds: any[]) => void  (called immediately on each toggle)
 *   placeholder — button text when nothing selected (default "All")
 *   searchable  — show search input (default true)
 *   width       — dropdown panel width in px (default 264)
 *   colorMap    — optional { [colorKey]: hexString } to resolve semantic color names
 */

// Default Bootstrap → CSS color map
const DEFAULT_COLOR_MAP = {
  primary:   '#0d6efd',
  secondary: '#6b7280',
  success:   '#16a34a',
  danger:    '#dc2626',
  warning:   '#d97706',
  info:      '#0891b2',
  light:     '#f1f5f9',
  dark:      '#1e293b',
};

const resolveColor = (color, colorMap = {}) => {
  if (!color) return null;
  if (color.startsWith('#')) return color;
  return { ...DEFAULT_COLOR_MAP, ...colorMap }[color] || '#6b7280';
};

// ── Sub-components ──────────────────────────────────────────────
const CheckBox = ({ checked, indeterminate, color }) => {
  const activeColor = color || '#3b82f6';
  return (
    <span style={{
      width: 15, height: 15, borderRadius: 3, flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: `1.5px solid ${checked || indeterminate ? activeColor : '#d1d5db'}`,
      background: checked ? activeColor : indeterminate ? `${activeColor}20` : '#fff',
      transition: 'all 0.1s',
    }}>
      {checked      && <FaCheck size={7} color="#fff" />}
      {indeterminate && <span style={{ width: 7, height: 2, background: activeColor, borderRadius: 1, display: 'block' }} />}
    </span>
  );
};
CheckBox.propTypes = {
  checked:      PropTypes.bool,
  indeterminate: PropTypes.bool,
  color:        PropTypes.string,
};

const ColorDot = ({ color, size = 8 }) => (
  <span style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: color || '#94a3b8', display: 'inline-block',
  }} />
);
ColorDot.propTypes = { color: PropTypes.string, size: PropTypes.number };

// ── Main component ──────────────────────────────────────────────
const GroupedCheckboxDropdown = ({
  groups,
  selectedIds,
  onChange,
  placeholder,
  searchable,
  width,
  colorMap,
  showGroupLabels,
}) => {
  const [open, setOpen]       = useState(false);
  const [search, setSearch]   = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const triggerRef            = useRef(null);
  const searchInputRef        = useRef(null);

  // Resolve all group/item colors upfront
  const resolvedGroups = useMemo(() =>
    groups.map((g) => ({
      ...g,
      _color: resolveColor(g.color, colorMap),
      items: g.items.map((it) => ({
        ...it,
        _color: resolveColor(it.color, colorMap),
      })),
    })),
    [groups, colorMap],
  );

  // Filter groups/items by search
  const visibleGroups = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return resolvedGroups;
    return resolvedGroups
      .map((g) => ({ ...g, items: g.items.filter((it) => it.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [resolvedGroups, search]);

  // All item values for "All" option
  const allValues = useMemo(() =>
    resolvedGroups.flatMap((g) => g.items.map((it) => it.value)),
    [resolvedGroups],
  );

  const totalCount    = allValues.length;
  const selectedCount = selectedIds.length;

  // Handle open/close
  const handleToggle = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const panelWidth = width + 2;
      const left = Math.min(rect.left, window.innerWidth - panelWidth - 8);
      const top  = window.innerHeight - rect.bottom >= 350
        ? rect.bottom + 6
        : Math.max(rect.top - 350 - 6, 8);
      setDropPos({ top, left });
    }
    setOpen((v) => !v);
    if (!open) {
      setSearch('');
      setTimeout(() => searchInputRef.current?.focus(), 60);
    }
  }, [open, width]);

  // Toggle single item
  const toggleItem = (value) =>
    onChange(
      selectedIds.includes(value)
        ? selectedIds.filter((id) => id !== value)
        : [...selectedIds, value],
    );

  // Toggle all items in a group
  const toggleGroup = (groupItems) => {
    const groupIds   = groupItems.map((it) => it.value);
    const allChecked = groupIds.every((id) => selectedIds.includes(id));
    onChange(
      allChecked
        ? selectedIds.filter((id) => !groupIds.includes(id))
        : [...new Set([...selectedIds, ...groupIds])],
    );
  };

  const clearAll = () => onChange([]);

  // Button label
  let btnLabel = placeholder || 'All';
  if (selectedCount > 0) {
    const firstName = resolvedGroups.flatMap((g) => g.items).find((it) => selectedIds.includes(it.value))?.label;
    btnLabel = selectedCount === 1
      ? (firstName || `1 selected`)
      : `${selectedCount} selected`;
  }

  const dropdown = open && ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div className="tw-fixed tw-inset-0 tw-z-[9998]" onClick={() => setOpen(false)} />

      <div
        className="tw-fixed tw-z-[9999] tw-bg-white tw-rounded-lg tw-shadow-2xl tw-border tw-border-slate-200 tw-flex tw-flex-col"
        style={{ top: dropPos.top, left: dropPos.left, width }}
      >
        {/* Search */}
        {searchable && (
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <FaSearch style={{ position: 'absolute', left: 8, color: '#94a3b8', pointerEvents: 'none' }} size={10} />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                style={{
                  width: '100%', paddingLeft: 26, paddingRight: 8, paddingTop: 5, paddingBottom: 5,
                  fontSize: '0.78rem', border: '1px solid #e2e8f0', borderRadius: 6,
                  outline: 'none', background: '#f8fafc', color: '#374151',
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  style={{ position: 'absolute', right: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                >
                  <FaTimes size={8} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* All row */}
        {!search && (
          <div
            onClick={clearAll}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
              cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
              background: selectedCount === 0 ? '#eff6ff' : 'transparent',
            }}
          >
            <CheckBox checked={selectedCount === 0} color="#3b82f6" />
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>All</span>
            <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{totalCount}</span>
          </div>
        )}

        {/* Groups */}
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: '4px 0' }}>
          {visibleGroups.length === 0 && (
            <div style={{ padding: '14px', textAlign: 'center', fontSize: '0.8rem', color: '#94a3b8' }}>
              No results
            </div>
          )}

          {visibleGroups.map((group) => {
            const groupIds    = group.items.map((it) => it.value);
            const allChecked  = groupIds.every((id) => selectedIds.includes(id));
            const someChecked = !allChecked && groupIds.some((id) => selectedIds.includes(id));
            const groupColor  = group._color || '#6366f1';

            return (
              <div key={group.key}>
                {/* Group header */}
                {showGroupLabels !== false && <div
                  onClick={() => toggleGroup(group.items)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7,
                    padding: '5px 12px 4px', cursor: 'pointer',
                    background: '#fafafa',
                  }}
                >
                  <CheckBox checked={allChecked} indeterminate={someChecked} color={groupColor} />
                  <ColorDot color={groupColor} size={7} />
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: groupColor, flex: 1,
                  }}>
                    {group.label}
                  </span>
                  <span style={{ fontSize: '0.68rem', color: '#cbd5e1', fontWeight: 500 }}>
                    {group.items.length}
                  </span>
                </div>}

                {/* Items */}
                {group.items.map((item) => {
                  const checked    = selectedIds.includes(item.value);
                  const itemColor  = item._color || groupColor;
                  return (
                    <div
                      key={item.value}
                      onClick={() => toggleItem(item.value)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '5px 12px 5px 30px', cursor: 'pointer',
                        background: checked ? `${itemColor}12` : 'transparent',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = checked ? `${itemColor}12` : 'transparent'; }}
                    >
                      <CheckBox checked={checked} color={itemColor} />
                      <ColorDot color={itemColor} size={7} />
                      <span style={{
                        flex: 1, fontSize: '0.8rem', color: checked ? itemColor : '#374151',
                        fontWeight: checked ? 600 : 400,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.label}
                      </span>
                      {item.meta && (
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8', marginLeft: 4 }}>{item.meta}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {selectedCount > 0 && (
          <div style={{
            borderTop: '1px solid #f1f5f9', padding: '6px 12px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: 600 }}>
              {selectedCount} selected
            </span>
            <button
              type="button"
              onClick={clearAll}
              style={{
                fontSize: '0.72rem', color: '#ef4444', fontWeight: 500,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '2px 6px', borderRadius: 4,
              }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>
    </>,
    document.body,
  );

  return (
    <div ref={triggerRef} style={{ display: 'inline-block' }}>
      <button
        type="button"
        onClick={handleToggle}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 32, padding: '0 10px', borderRadius: 8, cursor: 'pointer',
          fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap', border: '1px solid',
          transition: 'all 0.15s',
          ...(open || selectedCount > 0
            ? { background: '#eff6ff', borderColor: '#93c5fd', color: '#1d4ed8' }
            : { background: '#f8fafc', borderColor: '#d1d5db', color: '#64748b' }),
        }}
      >
        <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {btnLabel}
        </span>
        {selectedCount > 0 && (
          <span
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 14, height: 14, borderRadius: '50%',
              background: '#3b82f6', color: '#fff', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <FaTimes size={7} />
          </span>
        )}
        <FaChevronDown
          size={9}
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        />
      </button>
      {dropdown}
    </div>
  );
};

GroupedCheckboxDropdown.propTypes = {
  groups:      PropTypes.arrayOf(PropTypes.shape({
    key:   PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      meta:  PropTypes.string,
    })).isRequired,
  })).isRequired,
  selectedIds: PropTypes.array.isRequired,
  onChange:    PropTypes.func.isRequired,
  placeholder:     PropTypes.string,
  searchable:      PropTypes.bool,
  width:           PropTypes.number,
  colorMap:        PropTypes.object,
  showGroupLabels: PropTypes.bool,
};

GroupedCheckboxDropdown.defaultProps = {
  placeholder: 'All',
  searchable:  true,
  width:       264,
  colorMap:    {},
};

export default GroupedCheckboxDropdown;
